import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, Trash2 } from 'lucide-react';
import { TeacherAvailabilityTemplate } from '@/types/teacherAvailability';
import { TimeSlotCard } from './TimeSlotCard';
import { TimeSlotForm } from './TimeSlotForm';

interface TimeSlotFormData {
  startTime: string;
  endTime: string;
}

interface DayScheduleCardProps {
  dayOfWeek: number;
  dayName: string;
  slots: TeacherAvailabilityTemplate[];
  isEditing: boolean;
  tempSchedule: TimeSlotFormData;
  setTempSchedule: React.Dispatch<React.SetStateAction<TimeSlotFormData>>;
  onAddSlot: (dayOfWeek: number) => void;
  onEditSlot: (dayOfWeek: number, slot: TeacherAvailabilityTemplate) => void;
  onDeleteSlot: (slotId: string, dayOfWeek: number, slot: TeacherAvailabilityTemplate) => void;
  onDeleteAllSlots: (dayOfWeek: number) => void;
  onSave: () => void;
  onCancel: () => void;
  editingSlot: { dayOfWeek: number; slotId?: string } | null;
}

export function DayScheduleCard({
  dayOfWeek,
  dayName,
  slots,
  isEditing,
  tempSchedule,
  setTempSchedule,
  onAddSlot,
  onEditSlot,
  onDeleteSlot,
  onDeleteAllSlots,
  onSave,
  onCancel,
  editingSlot
}: DayScheduleCardProps) {
  return (
    <Card className="p-3 md:p-4">
      <div className="space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="w-full sm:w-20 font-medium text-center sm:text-left">{dayName}</div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => onAddSlot(dayOfWeek)}
              className="flex items-center justify-center gap-1 text-xs sm:text-sm px-2 py-1 h-8"
            >
              <Plus size={12} />
              <span className="hidden xs:inline">เพิ่มช่วงเวลา</span>
              <span className="xs:hidden">เพิ่ม</span>
            </Button>
            {slots.length > 0 && (
              <Button 
                size="sm" 
                variant="destructive" 
                onClick={() => onDeleteAllSlots(dayOfWeek)}
                className="flex items-center justify-center gap-1 text-xs sm:text-sm px-2 py-1 h-8"
              >
                <Trash2 size={12} />
                <span className="hidden xs:inline">ลบทั้งหมด</span>
                <span className="xs:hidden">ลบ</span>
              </Button>
            )}
          </div>
        </div>

        {/* Existing time slots */}
        {slots.map((slot) => (
          <TimeSlotCard
            key={slot.id}
            slot={slot}
            dayOfWeek={dayOfWeek}
            onEdit={onEditSlot}
            onDelete={onDeleteSlot}
          />
        ))}

        {/* Add/Edit form */}
        {isEditing && (
          <TimeSlotForm
            tempSchedule={tempSchedule}
            setTempSchedule={setTempSchedule}
            onSave={onSave}
            onCancel={onCancel}
            isEditing={!!editingSlot?.slotId}
          />
        )}

        {/* Show message when no slots */}
        {slots.length === 0 && !isEditing && (
          <div className="text-center text-muted-foreground py-2 text-sm">
            ไม่มีช่วงเวลาทำงาน
          </div>
        )}
      </div>
    </Card>
  );
}
